import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Wrench, ChevronRight, Copy, Check, Eraser } from "lucide-react";

// Extract & normalise MAC addresses from arbitrary pasted text.
// Accepts: AABBCCDDEEFF, AA:BB:CC:DD:EE:FF, AA-BB-..., aabb.ccdd.eeff, SEPAABBCCDDEEFF.
const MAC_RE = /(?:SEP)?((?:[0-9A-Fa-f]{2}[:\-]){5}[0-9A-Fa-f]{2}|(?:[0-9A-Fa-f]{4}\.){2}[0-9A-Fa-f]{4}|[0-9A-Fa-f]{12})/g;

function normalise(raw) {
  const hex = raw.replace(/^SEP/i, "").replace(/[:\-.]/g, "").toUpperCase();
  if (hex.length !== 12 || /[^0-9A-F]/.test(hex)) return null;
  return {
    hex,
    sep: "SEP" + hex,
    colon: hex.match(/.{2}/g).join(":"),
    dotted: hex.match(/.{4}/g).join(".").toLowerCase(),
  };
}

const CopyBtn = ({ text, testId }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <button onClick={copy} data-testid={testId} className="h-7 w-7 flex items-center justify-center rounded-md text-zinc-500 hover:text-blue-400 hover:bg-zinc-800 transition-colors">
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
};

export default function Tools() {
  const [input, setInput] = useState("");

  const parsed = useMemo(() => {
    const matches = input.match(MAC_RE) || [];
    const seen = new Set();
    const out = [];
    for (const m of matches) {
      const n = normalise(m);
      if (n && !seen.has(n.hex)) { seen.add(n.hex); out.push(n); }
    }
    return out;
  }, [input]);

  const invalidCount = (input.match(/[0-9A-Fa-f]{12,}/g) || []).length;

  const copyAll = (field, label) => {
    if (!parsed.length) { toast.error("Nothing to copy"); return; }
    navigator.clipboard.writeText(parsed.map((p) => p[field]).join("\n"));
    toast.success(`Copied ${parsed.length} ${label}`);
  };

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> tools <ChevronRight className="h-3 w-3" /> mac parser
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-5xl">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
          <Wrench className="h-4 w-4" /> Bulk MAC / Device Parser
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">MAC Address Formatter</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          Paste any messy list — CDP output, spreadsheets, emails — and extract clean, de-duplicated MAC
          addresses in every format you need: <span className="font-mono text-zinc-300">SEP</span> device names for
          BAT/CUCM provisioning, colon and Cisco dotted notation.
        </p>

        <div className="grid lg:grid-cols-2 gap-5 mt-6">
          {/* Input */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Paste device list</span>
              {input && (
                <button onClick={() => setInput("")} data-testid="clear-input-btn" className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-red-400">
                  <Eraser className="h-3 w-3" /> Clear
                </button>
              )}
            </div>
            <textarea
              data-testid="mac-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={"e.g.\n00:1B:2F:AA:BB:CC\nSEP001122334455\n001b.2faa.bbcc\nPhone-3F  ec44.7601.abcd"}
              className="w-full h-72 rounded-lg border border-zinc-800 bg-black p-4 font-mono text-[13px] text-emerald-300 focus:outline-none focus:border-blue-600/50 resize-none"
            />
            <div className="mt-2 text-[12px] text-zinc-500 font-mono" data-testid="parse-count">
              {parsed.length} unique MAC{parsed.length !== 1 ? "s" : ""} found
            </div>
          </div>

          {/* Output */}
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mr-auto">Results</span>
              <button onClick={() => copyAll("sep", "SEP names")} data-testid="copy-all-sep-btn" className="text-[11px] px-2.5 py-1 rounded-md border border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-blue-600/50 hover:text-blue-400">Copy all SEP</button>
              <button onClick={() => copyAll("colon", "colon MACs")} data-testid="copy-all-colon-btn" className="text-[11px] px-2.5 py-1 rounded-md border border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-blue-600/50 hover:text-blue-400">Copy all colon</button>
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-900 overflow-hidden h-72 overflow-y-auto" data-testid="mac-results">
              {parsed.length === 0 ? (
                <div className="p-6 text-zinc-600 font-mono text-sm">Paste a list on the left to see formatted results…</div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {parsed.map((p) => (
                    <div key={p.hex} data-testid={`mac-row-${p.hex}`} className="flex items-center gap-3 px-4 py-2.5">
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-[13px] text-white">{p.sep}</div>
                        <div className="flex flex-wrap gap-x-4 text-[11px] text-zinc-500 font-mono mt-0.5">
                          <span>{p.colon}</span>
                          <span>{p.dotted}</span>
                        </div>
                      </div>
                      <CopyBtn text={p.sep} testId={`copy-sep-${p.hex}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>
            {invalidCount > parsed.length && (
              <div className="mt-2 text-[12px] text-amber-400/80 font-mono">
                Some hex-like strings were skipped (not valid 12-digit MACs).
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
